import { useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { Controller, useForm } from 'react-hook-form'
import { useQuery } from '@tanstack/react-query'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { toast } from 'sonner'
import { Eye, EyeOff, Store } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Spinner } from '@/components/ui/spinner'
import { authApi } from '@/services/auth'
import { buildSignupEmailVerificationPath, isSignupEmailOtpVerificationContext } from '@/lib/auth-verification'
import { orgTreeApi } from '@/services/org-tree'
import { buildPostSelectionPath } from '@/lib/auth-redirect'
import { useSelectedBackendServerUrl } from '@/lib/backend-servers'
import { getErrorMessage } from '@/lib/api'
import { AuthTermsDisclosure } from '@/components/auth/auth-terms-disclosure'
import { BackendServerSelector } from '@/components/auth/backend-server-selector'
import { isRootOrgServiceInstance } from '@/lib/org-hierarchy-from-instance'
import { useRefetchOnReconnect } from '@/hooks/use-refetch-on-reconnect'
import { getAuthThemeServiceInstanceId, useAuthTheme } from '@/lib/auth-theme'
import { resolveServiceInstanceAuthLogos } from '@/lib/auth-branding'
import { TenantAuthShell } from '@/components/auth/tenant-auth-shell'

const PHONE_NUMBER_SAMPLE_PLACEHOLDER = '9876543210'

const normalizePhoneForCountry = (
  rawPhone: string,
  country: { phone_number_mask?: string | null; dial_code?: string | null } | null | undefined,
) => {
  const mask = String(country?.phone_number_mask ?? '').trim()
  if (!mask || !mask.includes('#')) {
    return null
  }

  const expectedLength = (mask.match(/#/g) ?? []).length
  const digitsOnly = String(rawPhone ?? '').replace(/\D/g, '')
  if (!digitsOnly) {
    return null
  }

  const dialCodeDigits = String(country?.dial_code ?? '').replace(/\D/g, '')
  const candidates = [digitsOnly]
  if (digitsOnly.startsWith('00') && digitsOnly.length > 2) {
    candidates.push(digitsOnly.slice(2))
  }

  for (const candidateRaw of candidates) {
    let candidate = candidateRaw
    if (candidate.length === expectedLength) {
      return candidate
    }

    if (dialCodeDigits && candidate.startsWith(dialCodeDigits)) {
      const localNumber = candidate.slice(dialCodeDigits.length)
      if (localNumber.length === expectedLength) {
        return localNumber
      }
      candidate = localNumber
    }

    while (candidate.length > expectedLength && candidate.startsWith('0')) {
      candidate = candidate.slice(1)
    }

    if (candidate.length === expectedLength) {
      return candidate
    }
  }

  return null
}

const vendorSignupSchema = z
  .object({
    vendor_name: z.string().trim().min(1, 'Vendor name is required'),
    branch_name: z.string().trim().min(1, 'Branch name is required'),
    service_variant_id: z.string().trim().min(1, 'App type is required'),
    email: z.string().email('Please enter a valid email address'),
    phone_number: z.string().trim().min(1, 'Phone number is required'),
    country_id: z.string().trim().min(1, 'Country is required'),
    password: z
      .string()
      .min(8, 'Password must be at least 8 characters')
      .regex(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/, 'Password must contain uppercase, lowercase, and number'),
    password_confirm: z.string(),
    accept_user_policy: z.boolean().refine((val) => val === true, {
      message: 'You must accept the user policy to continue',
    }),
  })
  .refine((data) => data.password === data.password_confirm, {
    message: 'Passwords do not match',
    path: ['password_confirm'],
  })

type VendorSignupFormData = z.infer<typeof vendorSignupSchema>

export default function SignupVendorPage() {
  const { orgInstanceId } = useParams<{ orgInstanceId: string }>()
  const navigate = useNavigate()
  const selectedBackendServerUrl = useSelectedBackendServerUrl()
  const authTheme = useAuthTheme()
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  const {
    register,
    handleSubmit,
    control,
    setError,
    setValue,
    watch,
    clearErrors,
    formState: { errors },
  } = useForm<VendorSignupFormData>({
    resolver: zodResolver(vendorSignupSchema),
    defaultValues: {
      country_id: '94',
      accept_user_policy: false,
    },
  })

  const selectedCountryId = watch('country_id')

  const themeOrgInstanceId = authTheme.locked ? getAuthThemeServiceInstanceId(authTheme) : ''
  const effectiveOrgInstanceId = themeOrgInstanceId || orgInstanceId || ''

  const orgQuery = useQuery({
    queryKey: ['vendor-signup-org', selectedBackendServerUrl, effectiveOrgInstanceId],
    queryFn: () => orgTreeApi.resolvePublicSignupServiceInstance(effectiveOrgInstanceId),
    enabled: !!effectiveOrgInstanceId,
    staleTime: 60_000,
  })

  useRefetchOnReconnect({
    enabled: Boolean(effectiveOrgInstanceId) && orgQuery.isError,
    refetch: orgQuery.refetch,
  })

  const permittedServicesQuery = useQuery({
    queryKey: ['vendor-signup-services', selectedBackendServerUrl, effectiveOrgInstanceId],
    queryFn: () => orgTreeApi.listPublicPermittedServices(effectiveOrgInstanceId),
    enabled: !!effectiveOrgInstanceId,
    staleTime: 60_000,
  })

  const countriesQuery = useQuery({
    queryKey: ['org-tree-countries', selectedBackendServerUrl],
    queryFn: orgTreeApi.listCountries,
    staleTime: 60_000,
  })

  useEffect(() => {
    if (!authTheme.locked || !themeOrgInstanceId || orgInstanceId === themeOrgInstanceId) {
      return
    }

    navigate(`/signup/vendor/${encodeURIComponent(themeOrgInstanceId)}`, { replace: true })
  }, [authTheme.locked, navigate, orgInstanceId, themeOrgInstanceId])

  const organization = (authTheme.locked ? authTheme.organization : null) ?? orgQuery.data
  const orgCountryId = organization?.country_id != null ? String(organization.country_id) : ''
  const isRootOrganizationSignup = isRootOrgServiceInstance(organization)
  const { sidePanelLogos, welcomeLogos } = resolveServiceInstanceAuthLogos(organization)
  const primaryColor = organization?.primary_color || authTheme.primaryColor

  useEffect(() => {
    if (orgCountryId) {
      if (selectedCountryId !== orgCountryId) {
        setValue('country_id', orgCountryId, { shouldValidate: true, shouldDirty: false })
      }
      return
    }

    if (selectedCountryId || !countriesQuery.data?.length) {
      return
    }

    setValue('country_id', String(countriesQuery.data[0].id), { shouldValidate: false, shouldDirty: false })
  }, [countriesQuery.data, orgCountryId, selectedCountryId, setValue])

  const orgName = organization?.name ?? organization?.instance_id ?? effectiveOrgInstanceId
  const termsText = String(organization?.terms_and_conditions || '').trim()
  const privacyText = String(organization?.privacy_policy || '').trim()

  const onSubmit = async (data: VendorSignupFormData) => {
    if (!effectiveOrgInstanceId) {
      toast.error('Organization not found.')
      return
    }

    clearErrors('phone_number')

    const normalizedPhone = normalizePhoneForCountry(data.phone_number, selectedCountry)
    if (!normalizedPhone) {
      const expectedDigits = (selectedCountry?.phone_number_mask?.match(/#/g) ?? []).length
      const rawDial = selectedCountry?.dial_code ? selectedCountry.dial_code.replace(/\D/g, '') : ''
      const dialCode = rawDial ? `+${rawDial}` : ''
      const hint = expectedDigits > 0
        ? `Enter ${expectedDigits} digits${dialCode ? ` (without ${dialCode})` : ''}.`
        : `Enter your local mobile number${dialCode ? ` without ${dialCode}` : ''}.`

      setError('phone_number', {
        type: 'validate',
        message: `Invalid phone number. ${hint}`,
      })
      return
    }

    setIsLoading(true)
    try {
      const response = await authApi.signup({
        signup_type: 'vendor',
        vendor_name: data.vendor_name,
        branch_name: data.branch_name,
        service_variant_id: data.service_variant_id,
        org_instance_id: effectiveOrgInstanceId,
        email: data.email,
        country_id: Number(data.country_id),
        phone_number: normalizedPhone,
        password: data.password,
        accept_user_policy: data.accept_user_policy,
      })

      const loginTenant = response.data.service_instance_id

      if (isSignupEmailOtpVerificationContext(response.data.context) && data.email.trim()) {
        navigate(buildSignupEmailVerificationPath({
          email: data.email.trim(),
          tenant: loginTenant,
          orgInstanceId: response.data.service_instance_id || effectiveOrgInstanceId,
          otpLength: response.data.otp_length,
        }), { replace: true })
        return
      }

      if (loginTenant) {
        try {
          const loginResponse = await authApi.login({
            tenant: loginTenant,
            email: data.email,
            password: data.password,
          })

          if (loginResponse.data?.access_token) {
            toast.success('Vendor account created! Select a post to continue.')
            navigate(buildPostSelectionPath(loginResponse.data.service_instance_id || loginTenant), { replace: true })
            return
          }
        } catch {
          // Signup succeeded; fall through to sign-in redirect.
        }
      }

      toast.success('Vendor account created! Please sign in.')
      const loginParams = new URLSearchParams()
      if (loginTenant) {
        loginParams.set('tenant', loginTenant)
      } else if (effectiveOrgInstanceId) {
        loginParams.set('tenant', effectiveOrgInstanceId)
      }
      navigate(loginParams.toString() ? `/login?${loginParams.toString()}` : '/login')
    } catch (error) {
      toast.error(getErrorMessage(error))
    } finally {
      setIsLoading(false)
    }
  }

  const servicesUnavailable =
    !permittedServicesQuery.isLoading && (permittedServicesQuery.isError || !permittedServicesQuery.data?.length)
  const serviceTypeLabel = isRootOrganizationSignup ? 'Available app' : 'App type'
  const serviceTypeHelperText = isRootOrganizationSignup
    ? 'Select the available app under this root organization for the vendor branch.'
    : null
  const selectedCountry = countriesQuery.data?.find((country) => String(country.id) === String(selectedCountryId || ''))

  return (
    <TenantAuthShell
      organizationName={orgName || 'Organization'}
      sidePanelLogos={sidePanelLogos}
      welcomeLogos={welcomeLogos}
      primaryColor={primaryColor}
      icon={Store}
      logoPosition="left"
      eyebrow=""
      title="Vendor Signup"
      description=""
      panelTitle=""
      panelDescription=""
      contentMaxWidth="max-w-xl"
    >
        <Card className="border-0 bg-transparent shadow-none">
          <CardHeader className="sr-only">
            <CardTitle>Vendor signup</CardTitle>
            <CardDescription>Create a vendor branch and your admin account for the service dashboard.</CardDescription>
          </CardHeader>
          <CardContent>
            <BackendServerSelector disabled={isLoading} className="mb-4" />
            {orgQuery.isLoading ? (
              <div className="flex items-center justify-center py-8">
                <Spinner />
              </div>
            ) : orgQuery.isError ? (
              <div className="space-y-4 text-center">
                <p className="text-sm text-destructive">Unable to load organization details due to a connection problem.</p>
                <Button variant="outline" onClick={() => {
                  void orgQuery.refetch()
                }}>
                  Retry
                </Button>
                {!authTheme.locked ? (
                  <Button variant="ghost" asChild>
                    <Link to="/signup/vendor">Try a different organization</Link>
                  </Button>
                ) : null}
              </div>
            ) : (!orgQuery.isLoading && !organization) ? (
              <div className="space-y-4 text-center">
                <p className="text-sm text-destructive">Organization not found or is no longer active.</p>
                {!authTheme.locked ? (
                  <Button variant="outline" asChild>
                    <Link to="/signup/vendor">Try a different organization</Link>
                  </Button>
                ) : null}
              </div>
            ) : (
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="vendor_name">Vendor name</Label>
                  <Input
                    id="vendor_name"
                    placeholder="Jane Doe"
                    disabled={isLoading}
                    {...register('vendor_name')}
                    aria-invalid={!!errors.vendor_name}
                  />
                  {errors.vendor_name ? <p className="text-sm text-destructive">{errors.vendor_name.message}</p> : null}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="branch_name">Branch name</Label>
                  <Input
                    id="branch_name"
                    placeholder="Downtown Store"
                    disabled={isLoading}
                    {...register('branch_name')}
                    aria-invalid={!!errors.branch_name}
                  />
                  {errors.branch_name ? <p className="text-sm text-destructive">{errors.branch_name.message}</p> : null}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="service_variant_id">{serviceTypeLabel}</Label>
                  <input type="hidden" {...register('service_variant_id')} />
                  <Select
                    onValueChange={(value) => setValue('service_variant_id', value, { shouldValidate: true, shouldDirty: true })}
                    disabled={isLoading || permittedServicesQuery.isLoading || servicesUnavailable}
                  >
                    <SelectTrigger id="service_variant_id" aria-invalid={!!errors.service_variant_id} className="w-full">
                      <SelectValue
                        placeholder={
                          permittedServicesQuery.isLoading
                            ? 'Loading services...'
                            : servicesUnavailable
                              ? 'No services available'
                              : 'Select a service type'
                        }
                      />
                    </SelectTrigger>
                    <SelectContent>
                      {(permittedServicesQuery.data ?? []).map((svc) => (
                        <SelectItem key={String(svc.id)} value={String(svc.id)}>
                          {svc.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {serviceTypeHelperText ? <p className="text-sm text-muted-foreground">{serviceTypeHelperText}</p> : null}
                  {servicesUnavailable && !errors.service_variant_id ? (
                    <p className="text-sm text-muted-foreground">
                      This organization has no available services for vendor signup.
                    </p>
                  ) : null}
                  {errors.service_variant_id ? (
                    <p className="text-sm text-destructive">{errors.service_variant_id.message}</p>
                  ) : null}
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="country_id">Country</Label>
                    <input type="hidden" {...register('country_id')} />
                    <Select
                      value={selectedCountryId || ''}
                      onValueChange={(value) => setValue('country_id', value, { shouldValidate: true, shouldDirty: true })}
                      disabled
                    >
                      <SelectTrigger id="country_id" aria-invalid={!!errors.country_id} className="w-full">
                        <SelectValue placeholder={countriesQuery.isLoading ? 'Loading...' : 'Select'} />
                      </SelectTrigger>
                      <SelectContent>
                        {(countriesQuery.data ?? []).map((country) => (
                          <SelectItem key={String(country.id)} value={String(country.id)}>
                            {[country.country_name, country.dial_code].filter(Boolean).join(' ')}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {errors.country_id ? <p className="text-sm text-destructive">{errors.country_id.message}</p> : null}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="phone_number">Phone number</Label>
                    <Input
                      id="phone_number"
                      type="tel"
                      placeholder={PHONE_NUMBER_SAMPLE_PLACEHOLDER}
                      autoComplete="tel"
                      disabled={isLoading}
                      {...register('phone_number')}
                      aria-invalid={!!errors.phone_number}
                    />
                    {errors.phone_number ? <p className="text-sm text-destructive">{errors.phone_number.message}</p> : null}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="name@example.com"
                    autoComplete="email"
                    disabled={isLoading}
                    {...register('email')}
                    aria-invalid={!!errors.email}
                  />
                  {errors.email ? <p className="text-sm text-destructive">{errors.email.message}</p> : null}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password">Password</Label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Create a password"
                      autoComplete="new-password"
                      disabled={isLoading}
                      {...register('password')}
                      aria-invalid={!!errors.password}
                    />
                    <button
                      type="button"
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      onClick={() => setShowPassword((v) => !v)}
                      tabIndex={-1}
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                  {errors.password ? <p className="text-sm text-destructive">{errors.password.message}</p> : null}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password_confirm">Confirm password</Label>
                  <div className="relative">
                    <Input
                      id="password_confirm"
                      type={showConfirmPassword ? 'text' : 'password'}
                      placeholder="Confirm your password"
                      autoComplete="new-password"
                      disabled={isLoading}
                      {...register('password_confirm')}
                      aria-invalid={!!errors.password_confirm}
                    />
                    <button
                      type="button"
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      onClick={() => setShowConfirmPassword((v) => !v)}
                      tabIndex={-1}
                    >
                      {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                  {errors.password_confirm ? <p className="text-sm text-destructive">{errors.password_confirm.message}</p> : null}
                </div>

                <div className="rounded-lg border border-border p-3">
                  <Controller
                    name="accept_user_policy"
                    control={control}
                    render={({ field }) => (
                      <div className="flex items-start gap-3">
                        <Checkbox
                          id="accept_user_policy"
                          checked={field.value}
                          onCheckedChange={(checked) => field.onChange(checked === true)}
                          aria-invalid={!!errors.accept_user_policy}
                        />
                        <div className="space-y-1">
                          <Label htmlFor="accept_user_policy" className="text-sm leading-5">
                            I accept the user policy and organization terms for this vendor account.
                          </Label>
                          <AuthTermsDisclosure
                            termsText={termsText}
                            privacyText={privacyText}
                            emptyMessage="No detailed policy text has been published on this signup page yet. Continue only if you have already reviewed the organization's vendor user policy."
                          />
                          {errors.accept_user_policy ? <p className="text-sm text-destructive">{errors.accept_user_policy.message}</p> : null}
                        </div>
                      </div>
                    )}
                  />
                </div>

                <Button type="submit" className="w-full" disabled={isLoading || servicesUnavailable}>
                  {isLoading ? <Spinner className="mr-2" /> : null}
                  Create vendor account
                </Button>
              </form>
            )}

            {!orgQuery.isLoading && !orgQuery.isError && organization ? (
              <div className="mt-6 space-y-2 text-center text-sm">
                {!authTheme.locked ? (
                  <div>
                    <span className="text-muted-foreground">Wrong organization? </span>
                    <Link to="/signup/vendor" className="font-medium text-primary hover:underline">
                      Go back
                    </Link>
                  </div>
                ) : null}
                <div>
                  <span className="text-muted-foreground">Already have an account? </span>
                  <Link to="/login" className="font-medium text-primary hover:underline">
                    Sign in
                  </Link>
                </div>
              </div>
            ) : null}
          </CardContent>
        </Card>
    </TenantAuthShell>
  )
}
